import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Alert, Platform, Dimensions, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import QuotationPrint from '../components/QuotationPrint';
import Tooltip from '../components/Tooltip';
import Toast from '../components/Toast';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/supabaseClient';
import { formatCurrency } from '../utils/formatters';

const INVOICE_TABLE = 'invoice_generator';
const QUOTATION_TABLE = 'quotation_maker';

export default function DashboardScreen() {
    const { session, openAuthModal } = useAuth();
    const navigation = useNavigation();
    const route = useRoute();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [filterPeriod, setFilterPeriod] = useState('all'); // 'all', '1m', '3m', '6m', 'custom'
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showCustomDateModal, setShowCustomDateModal] = useState(false);
    const [monthStats, setMonthStats] = useState({ totalCount: 0, paidCount: 0, dueCount: 0, totalAmount: 0 });

    // Mobile responsive
    const [screenData, setScreenData] = useState(Dimensions.get('window'));
    const isMobile = screenData.width < 768;

    useEffect(() => {
        const sub = Dimensions.addEventListener('change', ({ window }) => setScreenData(window));
        return () => sub?.remove?.();
    }, []);

    // Export modal state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');
    const [exportFormat, setExportFormat] = useState('csv');

    // Quotations state
    const [quotes, setQuotes] = useState([]);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [errorQuotes, setErrorQuotes] = useState('');
    const [infoQuotes, setInfoQuotes] = useState('');
    const [selectedQuoteMonth, setSelectedQuoteMonth] = useState('all');
    const [quoteMonthStats, setQuoteMonthStats] = useState({ totalCount: 0, totalAmount: 0 });
    const currencySymbol = '₹';
    const isWeb = Platform.OS === 'web';
    const CACHE_KEY = 'dashboard_invoices_cache';
    const QUOTES_CACHE_KEY = 'dashboard_quotes_cache';
    const STATUS_OPTIONS = ['draft', 'sent', 'accepted', 'rejected', 'expired'];
    const [pdfProps, setPdfProps] = useState(null);
    const [statusFlashId, setStatusFlashId] = useState(null);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('info');

    const showToast = (msg, type = 'info') => {
        setToastMessage(msg);
        setToastType(type);
        setToastVisible(true);
    };

    // When navigated from sidebar or other screens with sectionOnly,
    // render only the requested section as a dedicated view.
    const sectionParam = route?.params?.section;
    const normalizedSection = sectionParam === 'quotations' ? 'quotations' : 'invoices';
    const showInvoices = normalizedSection === 'invoices';
    const showQuotations = normalizedSection === 'quotations';

    // Ensure default is invoice-only when opened directly without params
    useEffect(() => {
        if (!route?.params?.section) {
            try { navigation.setParams({ section: 'invoices' }); } catch (_) { }
        }
    }, []);

    // If user is not logged in, open auth and route away from dashboard.
    useEffect(() => {
        if (!session?.user) {
            try { openAuthModal && openAuthModal(); } catch (_) { }
            try { navigation.replace('Home'); } catch (_) { }
        }
    }, [session?.user]);

    // If navigated with a section param, focus/scroll to that section (web).
    useEffect(() => {
        const section = route?.params?.section;
        if (Platform.OS === 'web' && section === 'quotations') {
            try {
                const el = document.getElementById('quotation-section');
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (_) { }
        }
        if (Platform.OS === 'web' && section === 'invoices') {
            try {
                const el = document.getElementById('invoice-section');
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (_) { }
        }
    }, [route?.params?.section]);

    const getMonthKey = (dIso, fallbackIso) => {
        const iso = dIso || fallbackIso || '';
        if (!iso || typeof iso !== 'string') return 'unknown';
        const parts = iso.split('T')[0].split('-');
        const y = parts[0];
        const m = parts[1];
        if (!y || !m) return 'unknown';
        return `${y}-${m}`; // YYYY-MM
    };

    const refreshInvoices = useCallback(async () => {
        if (!session?.user) return;
        setLoading(true);
        setError('');
        setInfo('');

        // Show cached rows immediately to reduce perceived wait
        if (isWeb) {
            try {
                const raw = window.localStorage.getItem(CACHE_KEY);
                if (raw) {
                    const cached = JSON.parse(raw);
                    if (Array.isArray(cached)) {
                        setInvoices(cached);
                    }
                }
            } catch (_) { }
        }

        // Main query with shorter timeout safeguard
        let query = supabase
            .from(INVOICE_TABLE)
            .select('id, title, invoice_number, invoice_date, currency, total, client, created_at, paid, billing_month')
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: false })
            .limit(200);
        // Note: avoid server-side filter on billing_month to support databases
        // that have not yet added the optional column. We compute month client-side.

        const selectPromise = query;
        const timeoutPromise = new Promise((resolve) => {
            // Allow a bit more time to avoid false timeouts on slower links
            setTimeout(() => resolve({ data: null, error: { message: 'Timed out contacting data service after 8s.', code: 'ETIMEOUT' } }), 8000);
        });

        let { data, error } = await Promise.race([selectPromise, timeoutPromise]);

        if (error) {
            if (error.code === 'ETIMEOUT') {
                // Fallback to direct fetch via PostgREST
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?user_id=eq.${session.user.id}&select=id,title,invoice_number,invoice_date,currency,total,client,created_at,paid,billing_month&order=created_at.desc&limit=200`;
                try {
                    const headers = {
                        apikey: SUPABASE_ANON_KEY,
                        Accept: 'application/json',
                        Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                    };
                    const resp = await fetch(url, { headers });
                    const text = await resp.text();
                    let json = null;
                    try { json = JSON.parse(text); } catch (_) { }
                    if (resp.ok && Array.isArray(json)) {
                        const rows = (json || []).map((r) => ({
                            ...r,
                            billing_month: r.billing_month || getMonthKey(r.invoice_date, r.created_at),
                            paid: !!r.paid,
                        }));
                        setInvoices(rows);
                        if (isWeb) {
                            try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(rows)); } catch (_) { }
                        }
                        setLoading(false);
                        return;
                    }
                    // Surface status and a hint to help troubleshooting
                    setError(`Fetch fallback failed (${resp.status}). Please reload or check network.`);
                } catch (e) {
                    setError(e?.message || 'Fetch fallback failed.');
                }
            } else {
                setError(error.message);
            }
        } else {
            const rows = (data || []).map((r) => ({
                ...r,
                billing_month: r.billing_month || getMonthKey(r.invoice_date, r.created_at),
                paid: !!r.paid,
            }));
            setInvoices(rows);
            if (isWeb) {
                try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(rows)); } catch (_) { }
            }
        }
        setLoading(false);
    }, [session?.user]);

    useEffect(() => { refreshInvoices(); }, [refreshInvoices]);
    // Also refresh whenever the dashboard gains focus (e.g., after navigating from save)
    useFocusEffect(
        React.useCallback(() => {
            refreshInvoices();
            return () => { };
        }, [refreshInvoices])
    );

    // Helper to get date cutoff based on filter period
    const getDateCutoff = (period) => {
        const now = new Date();
        if (period === '1m') {
            now.setMonth(now.getMonth() - 1);
        } else if (period === '3m') {
            now.setMonth(now.getMonth() - 3);
        } else if (period === '6m') {
            now.setMonth(now.getMonth() - 6);
        } else {
            return null; // 'all' or 'custom'
        }
        return now.toISOString().split('T')[0];
    };

    // Filter invoices based on selected period
    const filterInvoicesByPeriod = (rows) => {
        if (filterPeriod === 'all') return rows;

        if (filterPeriod === 'custom') {
            return rows.filter(r => {
                const date = r.invoice_date || r.created_at?.split('T')[0] || '';
                return (!customStartDate || date >= customStartDate) && (!customEndDate || date <= customEndDate);
            });
        }

        const cutoff = getDateCutoff(filterPeriod);
        if (!cutoff) return rows;

        return rows.filter(r => {
            const date = r.invoice_date || r.created_at?.split('T')[0] || '';
            return date >= cutoff;
        });
    };

    // Compute stats whenever filter or invoices change
    useEffect(() => {
        let filtered = invoices;
        if (filterPeriod === 'custom') {
            filtered = invoices.filter(r => {
                const date = r.invoice_date || r.created_at?.split('T')[0] || '';
                return (!customStartDate || date >= customStartDate) && (!customEndDate || date <= customEndDate);
            });
        } else if (filterPeriod !== 'all') {
            const now = new Date();
            if (filterPeriod === '1m') now.setMonth(now.getMonth() - 1);
            else if (filterPeriod === '3m') now.setMonth(now.getMonth() - 3);
            else if (filterPeriod === '6m') now.setMonth(now.getMonth() - 6);
            const cutoff = now.toISOString().split('T')[0];
            filtered = invoices.filter(r => {
                const date = r.invoice_date || r.created_at?.split('T')[0] || '';
                return date >= cutoff;
            });
        }
        const totalCount = filtered.length;
        const paidCount = filtered.filter((r) => !!r.paid).length;
        const dueCount = totalCount - paidCount;
        const totalAmount = filtered.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);
        setMonthStats({ totalCount, paidCount, dueCount, totalAmount });
    }, [filterPeriod, customStartDate, customEndDate, invoices]);

    // Quotations helpers
    const computeQuoteMonthStats = (rows, month) => {
        const filtered = month === 'all' ? rows : rows.filter((r) => (r.billing_month || getMonthKey(r.quote_date, r.created_at)) === month);
        const totalCount = filtered.length;
        const totalAmount = filtered.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);
        setQuoteMonthStats({ totalCount, totalAmount });
    };

    // Extract all unique months from quotes and sort descending
    const quoteMonthKeys = Array.from(
        new Set(quotes.map((r) => r.billing_month || getMonthKey(r.quote_date, r.created_at)))
    )
        .filter(m => m && m !== 'unknown')
        .sort((a, b) => b.localeCompare(a));

    const refreshQuotations = useCallback(async () => {
        if (!session?.user) return;
        setLoadingQuotes(true);
        setErrorQuotes('');
        setInfoQuotes('');

        // Show cached rows immediately to reduce perceived wait
        if (isWeb) {
            try {
                const raw = window.localStorage.getItem(QUOTES_CACHE_KEY);
                if (raw) {
                    const cached = JSON.parse(raw);
                    if (Array.isArray(cached)) {
                        setQuotes((prev) => {
                            const temps = (prev || []).filter((r) => String(r.id).startsWith('temp-'));
                            const merged = [...temps, ...cached.filter((r) => !String(r.id).startsWith('temp-'))];
                            computeQuoteMonthStats(merged, selectedQuoteMonth);
                            return merged;
                        });
                    }
                }
            } catch (_) { }
        }

        const monthFilter = selectedQuoteMonth !== 'all' ? selectedQuoteMonth : null;

        // Main query with shorter timeout safeguard
        let query = supabase
            .from(QUOTATION_TABLE)
            .select('id, title, quote_number, quote_date, currency, total, client, created_at, billing_month, status')
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: false })
            .limit(200);

        const selectPromise = query;
        const timeoutPromise = new Promise((resolve) => {
            setTimeout(() => resolve({ data: null, error: { message: 'Timed out contacting data service after 8s.', code: 'ETIMEOUT' } }), 8000);
        });

        let { data, error } = await Promise.race([selectPromise, timeoutPromise]);

        if (error) {
            if (error.code === 'ETIMEOUT') {
                // Fallback to direct fetch via PostgREST
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${QUOTATION_TABLE}?user_id=eq.${session.user.id}&select=id,title,quote_number,quote_date,currency,total,client,created_at,billing_month,status&order=created_at.desc&limit=200`;
                try {
                    const headers = {
                        apikey: SUPABASE_ANON_KEY,
                        Accept: 'application/json',
                        Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                    };
                    const resp = await fetch(url, { headers });
                    const text = await resp.text();
                    let json = null;
                    try { json = JSON.parse(text); } catch (_) { }
                    if (resp.ok && Array.isArray(json)) {
                        const rows = (json || []).map((r) => ({
                            ...r,
                            billing_month: r.billing_month || getMonthKey(r.quote_date, r.created_at),
                            status: r.status || 'draft',
                        }));
                        setQuotes((prev) => {
                            const temps = (prev || []).filter((r) => String(r.id).startsWith('temp-'));
                            const merged = [...temps, ...rows.filter((r) => !String(r.id).startsWith('temp-'))];
                            computeQuoteMonthStats(merged, selectedQuoteMonth);
                            if (isWeb) { try { window.localStorage.setItem(QUOTES_CACHE_KEY, JSON.stringify(merged)); } catch (_) { } }
                            return merged;
                        });
                        setLoadingQuotes(false);
                        return;
                    }
                    setErrorQuotes(`Fetch fallback failed (${resp.status}). Please reload or check network.`);
                } catch (e) {
                    setErrorQuotes(e?.message || 'Fetch fallback failed.');
                }
            } else {
                setErrorQuotes(error.message);
            }
        } else {
            const rows = (data || []).map((r) => ({
                ...r,
                billing_month: r.billing_month || getMonthKey(r.quote_date, r.created_at),
                status: r.status || 'draft',
            }));
            setQuotes((prev) => {
                const temps = (prev || []).filter((r) => String(r.id).startsWith('temp-'));
                const merged = [...temps, ...rows.filter((r) => !String(r.id).startsWith('temp-'))];
                computeQuoteMonthStats(merged, selectedQuoteMonth);
                if (isWeb) { try { window.localStorage.setItem(QUOTES_CACHE_KEY, JSON.stringify(merged)); } catch (_) { } }
                return merged;
            });
        }
        setLoadingQuotes(false);
    }, [session?.user, selectedQuoteMonth]);

    useEffect(() => { refreshQuotations(); }, [refreshQuotations]);
    useFocusEffect(
        React.useCallback(() => {
            refreshQuotations();
            return () => { };
        }, [refreshQuotations])
    );
    useEffect(() => { computeQuoteMonthStats(quotes, selectedQuoteMonth); }, [selectedQuoteMonth, quotes]);

    const confirmDeleteQuote = () => {
        if (Platform.OS === 'web') {
            try { return Promise.resolve(window.confirm('Delete this quotation permanently?')); } catch (_) { return Promise.resolve(true); }
        }
        return new Promise((resolve) => {
            Alert.alert('Delete quotation', 'Are you sure you want to delete this quotation?', [
                { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
                { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
            ]);
        });
    };

    const deleteQuoteRow = async (row) => {
        const ok = await confirmDeleteQuote();
        if (!ok) return;

        const prev = quotes;
        const next = quotes.filter((r) => r.id !== row.id);
        setQuotes(next);
        computeQuoteMonthStats(next, selectedQuoteMonth);
        if (isWeb) {
            try { window.localStorage.setItem(QUOTES_CACHE_KEY, JSON.stringify(next)); } catch (_) { }
        }

        const sdkDelete = supabase
            .from(QUOTATION_TABLE)
            .delete()
            .eq('id', row.id)
            .eq('user_id', session.user.id);

        const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));

        let result = await Promise.race([sdkDelete, timeout]);
        if (result && result.error && result.error.code === 'ETIMEOUT') {
            try {
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${QUOTATION_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
                const headers = {
                    apikey: SUPABASE_ANON_KEY,
                    Accept: 'application/json',
                    Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                    Prefer: 'return=minimal',
                };
                const resp = await fetch(url, { method: 'DELETE', headers });
                if (!resp.ok) throw new Error(`REST delete failed (${resp.status})`);
                setInfoQuotes('Quotation deleted.');
                await refreshQuotations();
                return;
            } catch (e) {
                setQuotes(prev);
                computeQuoteMonthStats(prev, selectedQuoteMonth);
                if (isWeb) {
                    try { window.localStorage.setItem(QUOTES_CACHE_KEY, JSON.stringify(prev)); } catch (_) { }
                }
                setErrorQuotes(e?.message || 'Delete failed');
                return;
            }
        }

        if (result && result.error) {
            // Attempt REST fallback on any SDK error
            try {
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${QUOTATION_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
                const headers = {
                    apikey: SUPABASE_ANON_KEY,
                    Accept: 'application/json',
                    Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                    Prefer: 'return=minimal',
                };
                const resp = await fetch(url, { method: 'DELETE', headers });
                if (!resp.ok) throw new Error(`REST delete failed (${resp.status})`);
                setInfoQuotes('Quotation deleted.');
                await refreshQuotations();
                return;
            } catch (e) {
                setQuotes(prev);
                computeQuoteMonthStats(prev, selectedQuoteMonth);
                if (isWeb) {
                    try { window.localStorage.setItem(QUOTES_CACHE_KEY, JSON.stringify(prev)); } catch (_) { }
                }
                setErrorQuotes(e?.message || result.error.message || 'Delete failed');
                return;
            }
        }

        setInfoQuotes('Quotation deleted.');
        await refreshQuotations();
    };

    const changeQuoteRowMonth = async (row, newMonth) => {
        try {
            setQuotes((prev) => prev.map((r) => (r.id === row.id ? { ...r, billing_month: newMonth } : r)));
            computeQuoteMonthStats(
                quotes.map((r) => (r.id === row.id ? { ...r, billing_month: newMonth } : r)),
                selectedQuoteMonth
            );

            const sdkUpdate = supabase
                .from(QUOTATION_TABLE)
                .update({ billing_month: newMonth })
                .eq('id', row.id)
                .eq('user_id', session.user.id);
            const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));
            let result = await Promise.race([sdkUpdate, timeout]);
            if (result && result.error) {
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${QUOTATION_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
                const headers = {
                    apikey: SUPABASE_ANON_KEY,
                    Accept: 'application/json',
                    Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                    Prefer: 'return=minimal',
                    'Content-Type': 'application/json',
                };
                const resp = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify({ billing_month: newMonth }) });
                if (!resp.ok) throw new Error(`Month update failed (${resp.status})`);
            }
            await refreshQuotations();
        } catch (e) {
            setErrorQuotes((e?.message || String(e)) + ' — Add billing_month column to enable month assignment.');
        }
    };

    // Helper: load the full quotation record by id (SDK + REST fallback)
    const loadFullQuoteById = async (id) => {
        const selectCols = 'id, title, quote_number, quote_date, valid_until, due_date, currency, total, seller, client, shipping, items, charges, notes, terms, contact_details, additional_info, logo_uri, signature_uri, columns_config, template_id, attachments, status';
        let { data, error } = await supabase
            .from(QUOTATION_TABLE)
            .select(selectCols)
            .eq('id', id)
            .eq('user_id', session.user.id)
            .single();
        if (error || !data) {
            try {
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${QUOTATION_TABLE}?id=eq.${id}&user_id=eq.${session.user.id}&select=${encodeURIComponent(selectCols)}`;
                const headers = { apikey: SUPABASE_ANON_KEY, Accept: 'application/json', Authorization: `Bearer ${token || SUPABASE_ANON_KEY}` };
                const resp = await fetch(url, { headers });
                const text = await resp.text();
                let json = null; try { json = JSON.parse(text); } catch (_) { }
                if (resp.ok && Array.isArray(json) && json.length) {
                    data = json[0];
                } else {
                    throw new Error(error?.message || `Failed to load quotation (${resp.status})`);
                }
            } catch (e) {
                throw e;
            }
        }
        return data;
    };

    // Helper: normalize row to QuotationPrint props
    const normalizePrintPropsFromRow = (row) => {
        const normalizeItems = (arr) => {
            if (!Array.isArray(arr)) return [];
            return arr.map((it, idx) => ({
                id: it.id || `line-${idx}`,
                name: it.name || it.item || it.description || '',
                description: it.description || '',
                hsnSac: it.hsnSac || it.hsn_sac || it.hsn || '',
                gstRate: it.gstRate ?? it.gst ?? it.tax ?? 0,
                qty: it.qty ?? it.quantity ?? 1,
                rate: it.rate ?? it.price ?? it.unitPrice ?? it.unit_price ?? 0,
                imageUri: it.imageUri || it.image_uri || '',
            }));
        };
        return {
            template: row?.template_id || 'classic',
            quote: {
                title: row?.title || 'Quotation',
                number: row?.quote_number || '',
                quoteDate: row?.quote_date || '',
                validUntil: row?.valid_until || '',
                dueDate: row?.due_date || '',
                currency: row?.currency || 'INR',
            },
            seller: row?.seller || {},
            client: row?.client || {},
            shipping: row?.shipping || { enabled: false },
            items: normalizeItems(row?.items || []),
            charges: row?.charges || {
                addlCharges: row?.additional_charges || 0,
                discount: row?.discount_total || 0,
                roundingMode: row?.rounding_mode || 'none',
                showTotalInWords: true,
            },
            columns: row?.columns_config || { showHSN: true, showGST: true, showImage: false },
            notes: row?.notes || '',
            terms: row?.terms || '',
            contactDetails: row?.contact_details || '',
            additionalInfo: row?.additional_info || '',
            logoUri: row?.logo_uri || '',
            signatureUri: row?.signature_uri || '',
        };
    };

    // Download quotation PDF
    const downloadQuoteRow = async (row) => {
        try {
            setErrorQuotes('');
            let full = null;
            try {
                full = await loadFullQuoteById(row.id);
            } catch (_) {
                // Fallback: proceed with the snapshot row if full load fails
                full = row || {};
            }
            if (!full || !full.id) throw new Error('Unable to load quotation to duplicate');
            const props = normalizePrintPropsFromRow(full);
            setPdfProps(props);
            // Wait a tick for off-screen render
            await new Promise((r) => setTimeout(r, 80));
            const target = document.getElementById('dashboard-quotation-print');
            if (!target) throw new Error('Preview not ready for PDF');
            const modCanvas = await import('html2canvas');
            const modPdf = await import('jspdf');
            const html2canvas = modCanvas.default;
            const { jsPDF } = modPdf;
            const canvas = await html2canvas(target, { scale: 1.8, useCORS: true, backgroundColor: '#ffffff' });
            const imgData = canvas.toDataURL('image/jpeg', 0.85);
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth - 40;
            const imgHeight = canvas.height * imgWidth / canvas.width;
            let y = 20;
            if (imgHeight <= pageHeight - 40) {
                pdf.addImage(imgData, 'JPEG', 20, y, imgWidth, imgHeight);
            } else {
                let heightLeft = imgHeight;
                let position = 20;
                while (heightLeft > 0) {
                    pdf.addImage(imgData, 'JPEG', 20, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                    if (heightLeft > 0) {
                        pdf.addPage();
                        position = 20 - (imgHeight - heightLeft);
                    }
                }
            }
            const filename = `Quotation_${full?.quote_number || 'MM'}.pdf`;
            pdf.save(filename);
            setInfoQuotes('PDF downloaded.');
        } catch (e) {
            setErrorQuotes(e?.message || 'Failed to download PDF');
        } finally {
            // Clear off-screen print
            setTimeout(() => setPdfProps(null), 200);
        }
    };

    // Duplicate quotation
    const duplicateQuoteRow = async (row) => {
        try {
            // Immediate feedback so users see the action start
            setInfoQuotes('Duplicating quotation…');
            showToast('Duplicating quotation…', 'info');
            setErrorQuotes('');
            // Use lightweight data to proceed immediately; avoid waiting for full load
            let full = row || {};
            if (typeof console !== 'undefined') {
                try { console.log('[dup] start', { row, full }); } catch (_) { }
            }
            const todayIso = new Date().toISOString().split('T')[0];
            const existingNums = quotes.map((q) => String(q.quote_number || '')).filter(Boolean);
            const baseNum = String(full.quote_number || 'Q').trim();
            // Append " Copy" to the end of quote number; ensure uniqueness
            let newNum = `${baseNum} Copy`;
            let idx = 1;
            while (existingNums.includes(newNum)) { newNum = `${baseNum} Copy ${idx++}`; }
            // Minimal payload to maximize compatibility across databases
            const dupMonth = getMonthKey(full.quote_date, full.created_at) || getMonthKey(todayIso, todayIso);
            const payload = {
                user_id: session.user.id,
                title: (full.title || 'Quotation'),
                quote_number: newNum,
                quote_date: full.quote_date || todayIso,
                currency: full.currency || 'INR',
                client: full.client || null,
                status: 'draft',
                billing_month: dupMonth,
            };
            // Optimistic UI: show the duplicate immediately while persisting in background
            const tempId = `temp-${Date.now()}`;
            const tempRow = {
                id: tempId,
                created_at: new Date().toISOString(),
                quote_number: payload.quote_number,
                quote_date: payload.quote_date,
                currency: payload.currency,
                total: 0,
                client: payload.client,
                billing_month: payload.billing_month,
                status: 'draft',
            };
            // Add optimistic row and update stats/cache based on the new array in one atomic set
            setQuotes((prev) => {
                const next = [tempRow, ...prev];
                computeQuoteMonthStats(next, selectedQuoteMonth);
                if (isWeb) { try { window.localStorage.setItem(QUOTES_CACHE_KEY, JSON.stringify(next)); } catch (_) { } }
                return next;
            });
            const sdkInsert = supabase
                .from(QUOTATION_TABLE)
                .insert(payload)
                .select('id, created_at, quote_number, quote_date, currency, total, client, billing_month, status')
                .single();
            const timeout = new Promise((resolve) => setTimeout(() => resolve({ data: null, error: { message: 'Timed out', code: 'ETIMEOUT' } }), 8000));
            let { data, error } = await Promise.race([sdkInsert, timeout]);
            if (error || !data) {
                if (typeof console !== 'undefined') {
                    try { console.warn('[dup] SDK insert failed, falling back', { error }); } catch (_) { }
                }
                const token = session?.access_token || session?.accessToken || null;
                // Progressive REST fallbacks
                const headers = { apikey: SUPABASE_ANON_KEY, Accept: 'application/json', Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' };
                const repUrl = `${SUPABASE_URL}/rest/v1/${QUOTATION_TABLE}?select=id,created_at,quote_number,quote_date`;
                // First: try representation insert (best case)
                let resp = await fetch(repUrl, { method: 'POST', headers: { ...headers, Prefer: 'return=representation' }, body: JSON.stringify(payload) });
                let text = await resp.text();
                let json = null; try { json = JSON.parse(text); } catch (_) { }
                if (resp.ok && json) {
                    data = Array.isArray(json) ? json[0] : json;
                } else {
                    if (typeof console !== 'undefined') {
                        try { console.warn('[dup] REST representation insert failed', { status: resp.status, body: text }); } catch (_) { }
                    }
                    // Second: try minimal insert, then fetch the inserted row by quote_number
                    const minimal = { user_id: session.user.id, title: payload.title, quote_number: payload.quote_number, quote_date: payload.quote_date };
                    const respMin = await fetch(repUrl, { method: 'POST', headers: { ...headers, Prefer: 'return=minimal' }, body: JSON.stringify(minimal) });
                    if (!respMin.ok) {
                        const body = await respMin.text();
                        try { console.error('[dup] REST minimal insert failed', { status: respMin.status, body }); } catch (_) { }
                        // Third: try SDK minimal insert
                        const sdkMinimal = supabase
                            .from(QUOTATION_TABLE)
                            .insert(minimal)
                            .select('id, created_at, quote_number, quote_date')
                            .single();
                        const mTimeout = new Promise((resolve) => setTimeout(() => resolve({ data: null, error: { message: 'Timed out', code: 'ETIMEOUT' } }), 8000));
                        const mRes = await Promise.race([sdkMinimal, mTimeout]);
                        if (mRes && mRes.data) {
                            data = mRes.data;
                        } else {
                            throw new Error('Duplicate failed — insert did not succeed');
                        }
                    } else {
                        // Fetch the inserted row by unique quote_number for immediate UI update
                        const fetchUrl = `${SUPABASE_URL}/rest/v1/${QUOTATION_TABLE}?user_id=eq.${session.user.id}&quote_number=eq.${encodeURIComponent(minimal.quote_number)}&select=id,created_at,quote_number,quote_date&limit=1`;
                        const respFetch = await fetch(fetchUrl, { headers });
                        const fetchText = await respFetch.text();
                        let fetchJson = null; try { fetchJson = JSON.parse(fetchText); } catch (_) { }
                        if (respFetch.ok && Array.isArray(fetchJson) && fetchJson.length) {
                            data = fetchJson[0];
                        } else {
                            // As a final fallback, refresh list and rely on latest data
                            await refreshQuotations();
                            setInfoQuotes(`Quotation duplicated as ${minimal.quote_number}.`);
                            showToast(`Quotation duplicated as ${minimal.quote_number}.`, 'success');
                            return;
                        }
                    }
                }
            }
            // Replace optimistic row with the real inserted row
            const newRow = {
                ...data,
                billing_month: data.billing_month || getMonthKey(data.quote_date, data.created_at),
                status: data.status || 'draft',
            };
            if (typeof console !== 'undefined') {
                try { console.log('[dup] success', { newRow }); } catch (_) { }
            }
            setQuotes((prev) => {
                const next = [newRow, ...prev.filter((r) => r.id !== tempId)];
                computeQuoteMonthStats(next, selectedQuoteMonth);
                if (isWeb) { try { window.localStorage.setItem(QUOTES_CACHE_KEY, JSON.stringify(next)); } catch (_) { } }
                return next;
            });
            setInfoQuotes(`Quotation duplicated as ${newRow.quote_number}.`);
            showToast(`Quotation duplicated as ${newRow.quote_number}.`, 'success');
            // Ensure latest server state reflected
            await refreshQuotations();
        } catch (e) {
            // Keep optimistic row visible so user sees something even if insert fails
            setQuotes((prev) => prev);
            computeQuoteMonthStats(quotes, selectedQuoteMonth);
            if (isWeb) { try { window.localStorage.setItem(QUOTES_CACHE_KEY, JSON.stringify(quotes)); } catch (_) { } }
            setErrorQuotes(e?.message || 'Failed to duplicate quotation');
            showToast(e?.message || 'Failed to duplicate quotation', 'error');
            if (typeof console !== 'undefined') {
                try { console.error('[dup] error', e); } catch (_) { }
            }
        }
    };

    // Change status with optimistic update
    const changeQuoteRowStatus = async (row, newStatus) => {
        try {
            if (!STATUS_OPTIONS.includes(newStatus)) throw new Error('Invalid status');
            setQuotes((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r)));
            // Trigger a quick flash animation on web so the change is visible
            if (isWeb) {
                setStatusFlashId(row.id);
                setTimeout(() => setStatusFlashId(null), 1200);
            }
            const sdkUpdate = supabase
                .from(QUOTATION_TABLE)
                .update({ status: newStatus })
                .eq('id', row.id)
                .eq('user_id', session.user.id);
            const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));
            let result = await Promise.race([sdkUpdate, timeout]);
            if (result && result.error) {
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${QUOTATION_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
                const headers = { apikey: SUPABASE_ANON_KEY, Accept: 'application/json', Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`, Prefer: 'return=minimal', 'Content-Type': 'application/json' };
                const resp = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify({ status: newStatus }) });
                if (!resp.ok) throw new Error(`Status update failed (${resp.status})`);
            }
            setInfoQuotes('Status updated.');
            showToast('Status updated.', 'success');
        } catch (e) {
            setErrorQuotes(e?.message || 'Failed to update status');
            showToast(e?.message || 'Failed to update status', 'error');
        }
    };

    // Email quotation functionality has been removed from the dashboard actions.

    const viewQuoteRow = (row) => {
        if (isWeb) {
            try { window.localStorage.setItem(`quote_prefetch_${row.id}`, JSON.stringify(row)); } catch (_) { }
        }
        navigation.navigate('QuotationMaker', { loadId: row.id, rowSnapshot: row });
    };

    const confirmDelete = () => {
        if (Platform.OS === 'web') {
            try { return Promise.resolve(window.confirm('Delete this invoice permanently?')); } catch (_) { return Promise.resolve(true); }
        }
        return new Promise((resolve) => {
            Alert.alert('Delete invoice', 'Are you sure you want to delete this invoice?', [
                { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
                { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
            ]);
        });
    };

    const deleteInvoiceRow = async (row) => {
        const ok = await confirmDelete();
        if (!ok) return;

        // Optimistic UI: remove immediately, update cache
        const prev = invoices;
        const next = invoices.filter((r) => r.id !== row.id);
        setInvoices(next);
        computeMonthStats(next, selectedMonth);
        if (isWeb) {
            try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch (_) { }
        }

        // Attempt SDK delete with short timeout; fall back to PostgREST
        const sdkDelete = supabase
            .from(INVOICE_TABLE)
            .delete()
            .eq('id', row.id)
            .eq('user_id', session.user.id);

        const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));

        let result = await Promise.race([sdkDelete, timeout]);
        if (result && result.error && result.error.code === 'ETIMEOUT') {
            // Fallback to REST delete respecting RLS
            try {
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${row.id}`;
                const headers = {
                    apikey: SUPABASE_ANON_KEY,
                    Accept: 'application/json',
                    Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                    Prefer: 'return=minimal',
                };
                const resp = await fetch(url, { method: 'DELETE', headers });
                if (!resp.ok) throw new Error(`REST delete failed (${resp.status})`);
                setInfo('Invoice deleted.');
                await refreshInvoices();
                return;
            } catch (e) {
                // Rollback optimistic change on failure
                setInvoices(prev);
                computeMonthStats(prev, selectedMonth);
                if (isWeb) {
                    try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(prev)); } catch (_) { }
                }
                setError(e?.message || 'Delete failed');
                return;
            }
        }

        if (result && result.error) {
            // Rollback optimistic change on failure
            setInvoices(prev);
            computeMonthStats(prev, selectedMonth);
            if (isWeb) {
                try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(prev)); } catch (_) { }
            }
            setError(result.error.message || 'Delete failed');
            return;
        }

        setInfo('Invoice deleted.');
        await refreshInvoices();
    };

    const togglePaidRow = async (row) => {
        try {
            const nextPaid = !row.paid;
            // Optimistic UI: update local row immediately
            setInvoices((prev) => prev.map((r) => (r.id === row.id ? { ...r, paid: nextPaid } : r)));
            computeMonthStats(
                invoices.map((r) => (r.id === row.id ? { ...r, paid: nextPaid } : r)),
                selectedMonth
            );

            // Try SDK update first with short timeout
            const sdkUpdate = supabase
                .from(INVOICE_TABLE)
                .update({ paid: nextPaid })
                .eq('id', row.id)
                .eq('user_id', session.user.id)
                .select('id, paid'); // ensure we know if any row was affected
            const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));
            let result = await Promise.race([sdkUpdate, timeout]);

            // Fallback to REST if SDK times out or errors
            if ((result && result.error) || !result?.data || (Array.isArray(result.data) && result.data.length === 0)) {
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
                const headers = {
                    apikey: SUPABASE_ANON_KEY,
                    Accept: 'application/json',
                    Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                    Prefer: 'return=representation',
                    'Content-Type': 'application/json',
                };
                const resp = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify({ paid: nextPaid }) });
                if (!resp.ok) throw new Error(`Paid toggle failed (${resp.status})`);
            }
            await refreshInvoices();
        } catch (e) {
            // Graceful: if column missing, surface actionable message
            setError((e?.message || String(e)) + ' — Add paid column to enable status toggles.');
        }
    };

    const changeRowMonth = async (row, newMonth) => {
        try {
            // Optimistic UI
            setInvoices((prev) => prev.map((r) => (r.id === row.id ? { ...r, billing_month: newMonth } : r)));
            computeMonthStats(
                invoices.map((r) => (r.id === row.id ? { ...r, billing_month: newMonth } : r)),
                selectedMonth
            );

            const sdkUpdate = supabase
                .from(INVOICE_TABLE)
                .update({ billing_month: newMonth })
                .eq('id', row.id)
                .eq('user_id', session.user.id);
            const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));
            let result = await Promise.race([sdkUpdate, timeout]);
            if (result && result.error) {
                const token = session?.access_token || session?.accessToken || null;
                const url = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
                const headers = {
                    apikey: SUPABASE_ANON_KEY,
                    Accept: 'application/json',
                    Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                    Prefer: 'return=minimal',
                    'Content-Type': 'application/json',
                };
                const resp = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify({ billing_month: newMonth }) });
                if (!resp.ok) throw new Error(`Month update failed (${resp.status})`);
            }
            await refreshInvoices();
        } catch (e) {
            setError((e?.message || String(e)) + ' — Add billing_month column to enable month assignment.');
        }
    };

    const sendInvoiceToClient = (row) => {
        const email = row?.client?.email || '';
        const subject = encodeURIComponent(`Invoice ${row?.invoice_number || ''}`);
        const body = encodeURIComponent(
            `Dear ${row?.client?.businessName || 'Client'},\n\nPlease find your invoice details below:\nInvoice No: ${row?.invoice_number || ''}\nInvoice Date: ${row?.invoice_date || ''}\nTotal: ${row?.total || ''} ${row?.currency || ''}\n\nBest regards,\nModern Munshiji`
        );
        const mailto = `mailto:${email}?subject=${subject}&body=${body}`;
        try { window.open(mailto, '_blank'); } catch (_) { }
    };

    const viewInvoiceRow = (row) => {
        // Cache snapshot for instant prefill on the generator screen
        if (isWeb) {
            try { window.localStorage.setItem(`invoice_prefetch_${row.id}`, JSON.stringify(row)); } catch (_) { }
        }
        navigation.navigate('InvoiceGenerator', { loadId: row.id, rowSnapshot: row });
    };

    const downloadInvoiceRow = (row) => {
        if (isWeb) {
            try { window.localStorage.setItem(`invoice_prefetch_${row.id}`, JSON.stringify(row)); } catch (_) { }
        }
        navigation.navigate('InvoiceGenerator', { loadId: row.id, download: true, rowSnapshot: row });
    };

    // Duplicate invoice as Draft with "Copy" suffix
    const duplicateInvoiceRow = async (row) => {
        try {
            setInfo('Duplicating invoice…');
            showToast('Duplicating invoice…', 'info');
            setError('');

            const token = session?.access_token || session?.accessToken || null;

            // 1) Load full invoice data via REST API (faster than SDK for big JSON fields)
            let fullRow = null;
            const loadUrl = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}&select=*`;
            const loadHeaders = {
                apikey: SUPABASE_ANON_KEY,
                Accept: 'application/json',
                Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
            };
            const loadResp = await fetch(loadUrl, { headers: loadHeaders });
            const loadText = await loadResp.text();
            try {
                const parsed = JSON.parse(loadText);
                if (Array.isArray(parsed) && parsed.length > 0) fullRow = parsed[0];
            } catch (_) { }

            if (!fullRow) {
                throw new Error('Failed to load invoice for duplication');
            }

            const todayIso = new Date().toISOString().split('T')[0];
            const origNumber = fullRow.invoice_number || '';
            const newNumber = origNumber.includes('Copy') ? origNumber : `${origNumber}-Copy`;

            // 2) Create full invoice payload with all data
            const newInvoice = {
                user_id: session.user.id,
                title: fullRow.title || 'Invoice',
                invoice_number: newNumber,
                invoice_date: todayIso,
                due_date: null,
                show_due_date: false,
                currency: fullRow.currency || 'INR',
                gst_type: fullRow.gst_type || 'IGST',
                seller: fullRow.seller || null,
                client: fullRow.client || null,
                shipping: fullRow.shipping || null,
                items: fullRow.items || null,
                charges: fullRow.charges || null,
                payment: fullRow.payment || null,
                notes: fullRow.notes || null,
                terms: fullRow.terms || null,
                contact_details: fullRow.contact_details || null,
                logo_uri: fullRow.logo_uri || null,
                attachments: fullRow.attachments || null,
                total: fullRow.total || 0,
                paid: false,
                billing_month: getMonthKey(todayIso, todayIso),
            };

            // 3) Insert via REST API
            const insertUrl = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}`;
            const insertHeaders = {
                apikey: SUPABASE_ANON_KEY,
                Accept: 'application/json',
                Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json',
                Prefer: 'return=representation',
            };
            const insertResp = await fetch(insertUrl, { method: 'POST', headers: insertHeaders, body: JSON.stringify(newInvoice) });
            const insertText = await insertResp.text();

            if (!insertResp.ok) {
                throw new Error(`Duplicate failed (${insertResp.status})`);
            }

            await refreshInvoices();
            setInfo(`Invoice duplicated as "${newNumber}" (Draft)`);
            showToast(`Invoice duplicated as "${newNumber}"`, 'success');
        } catch (e) {
            console.error('[dup invoice] error', e);
            setError(e?.message || 'Duplicate failed');
            showToast(e?.message || 'Duplicate failed', 'error');
        }
    };

    // Export invoices to CSV or Excel
    const exportInvoices = () => {
        try {
            let dataToExport = invoices;

            // Filter by date range if provided
            if (exportStartDate) {
                dataToExport = dataToExport.filter(inv => {
                    const date = inv.invoice_date || inv.created_at?.split('T')[0];
                    return date >= exportStartDate;
                });
            }
            if (exportEndDate) {
                dataToExport = dataToExport.filter(inv => {
                    const date = inv.invoice_date || inv.created_at?.split('T')[0];
                    return date <= exportEndDate;
                });
            }

            if (dataToExport.length === 0) {
                showToast('No invoices found for the selected date range', 'error');
                return;
            }

            // Prepare CSV data
            const headers = ['#', 'Invoice No', 'Date', 'Client', 'Total', 'Currency', 'Status', 'Month'];
            const rows = dataToExport.map((inv, idx) => [
                idx + 1,
                inv.invoice_number || '',
                inv.invoice_date || '',
                inv.client?.businessName || '',
                inv.total || 0,
                inv.currency || 'INR',
                inv.paid ? 'Paid' : 'Due',
                inv.billing_month || getMonthKey(inv.invoice_date, inv.created_at)
            ]);

            // Create CSV content
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            ].join('\n');

            // Download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const today = new Date().toISOString().split('T')[0];
            const filename = `invoices_export_${today}.${exportFormat === 'xlsx' ? 'csv' : 'csv'}`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setShowExportModal(false);
            showToast(`Exported ${dataToExport.length} invoices successfully!`, 'success');
        } catch (e) {
            console.error('Export failed:', e);
            showToast('Export failed', 'error');
        }
    };

    // Mobile card render for invoice
    const renderMobileInvoiceCard = (row, idx) => (
        <View key={row.id} style={styles.mobileCard}>
            <View style={styles.mobileCardHeader}>
                <Text style={styles.mobileCardTitle}>{row.invoice_number || '—'}</Text>
                <Text style={[styles.pill, { backgroundColor: row.paid ? '#DCFCE7' : '#FEE2E2', color: row.paid ? '#065f46' : '#7F1D1D' }]}>
                    {row.paid ? 'Paid' : 'Due'}
                </Text>
            </View>
            <View style={styles.mobileCardRow}>
                <Text style={styles.mobileCardLabel}>Date:</Text>
                <Text style={styles.mobileCardValue}>{row.invoice_date || '—'}</Text>
            </View>
            <View style={styles.mobileCardRow}>
                <Text style={styles.mobileCardLabel}>Client:</Text>
                <Text style={styles.mobileCardValue}>{row?.client?.businessName || '—'}</Text>
            </View>
            <View style={styles.mobileCardRow}>
                <Text style={styles.mobileCardLabel}>Total:</Text>
                <Text style={[styles.mobileCardValue, { fontWeight: '700' }]}>{formatCurrency(parseFloat(row.total) || 0, currencySymbol)}</Text>
            </View>
            <View style={styles.mobileCardActions}>
                <TouchableOpacity style={styles.iconButton} onPress={() => viewInvoiceRow(row)}>
                    <Ionicons name="eye-outline" size={18} color="#334155" />
                </TouchableOpacity>
                <TouchableOpacity style={styles.iconButton} onPress={() => duplicateInvoiceRow(row)}>
                    <Ionicons name="copy-outline" size={18} color="#334155" />
                </TouchableOpacity>
                <TouchableOpacity style={styles.iconButton} onPress={() => downloadInvoiceRow(row)}>
                    <Ionicons name="download-outline" size={18} color="#334155" />
                </TouchableOpacity>
                <TouchableOpacity style={styles.iconButton} onPress={() => togglePaidRow(row)}>
                    <Ionicons name={row.paid ? 'checkmark-done-outline' : 'alert-circle-outline'} size={18} color={row.paid ? '#065f46' : '#7F1D1D'} />
                </TouchableOpacity>
                <TouchableOpacity style={styles.iconButton} onPress={() => deleteInvoiceRow(row)}>
                    <Ionicons name="trash-outline" size={18} color="#7F1D1D" />
                </TouchableOpacity>
            </View>
        </View>
    );

    return (
        <View style={{ flex: 1 }}>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, gap: 16, minHeight: 700 }}>
                {showInvoices && (
                    <>
                        <View style={{ flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 12 }}>
                            {/* Invoices Section header anchor */}
                            {React.createElement('div', { id: 'invoice-section', style: { position: 'absolute', top: -20 } })}
                            <Text style={{ fontSize: 20, fontWeight: '700', color: '#0f172a' }}>Invoice Generator</Text>
                            {!!session?.user && (
                                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <TouchableOpacity style={styles.outlineButton} onPress={refreshInvoices}>
                                        <Ionicons name="refresh-outline" size={16} color="#4F46E5" />
                                        {!isMobile && <Text style={styles.outlineButtonText}>Refresh</Text>}
                                    </TouchableOpacity>
                                    <TouchableOpacity style={[styles.outlineButton, { borderColor: '#10B981' }]} onPress={() => setShowExportModal(true)}>
                                        <Ionicons name="download-outline" size={16} color="#10B981" />
                                        {!isMobile && <Text style={[styles.outlineButtonText, { color: '#10B981' }]}>Export</Text>}
                                    </TouchableOpacity>
                                    {/* Period Filter */}
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        {!isMobile && <Text style={styles.helperText}>Period</Text>}
                                        {React.createElement('select', {
                                            value: filterPeriod,
                                            onChange: (e) => {
                                                const val = e.target.value;
                                                if (val === 'custom') {
                                                    setShowCustomDateModal(true);
                                                }
                                                setFilterPeriod(val);
                                            },
                                            style: { padding: 6, borderRadius: 6, border: '1px solid #e5e7eb', backgroundColor: '#f8fafc', fontSize: 12 },
                                        }, [
                                            React.createElement('option', { key: 'all', value: 'all' }, 'All Time'),
                                            React.createElement('option', { key: '1m', value: '1m' }, 'Last 1 Month'),
                                            React.createElement('option', { key: '3m', value: '3m' }, 'Last 3 Months'),
                                            React.createElement('option', { key: '6m', value: '6m' }, 'Last 6 Months'),
                                            React.createElement('option', { key: 'custom', value: 'custom' },
                                                customStartDate || customEndDate
                                                    ? `Custom: ${customStartDate || '...'} to ${customEndDate || '...'}`
                                                    : 'Custom Range...'
                                            ),
                                        ])}
                                    </View>
                                </View>
                            )}
                        </View>
                        {!session?.user && (
                            <View style={styles.card}>
                                <Text style={{ fontSize: 14, color: '#334155' }}>Please login to view saved invoices.</Text>
                                <TouchableOpacity style={styles.primaryButton} onPress={openAuthModal}>
                                    <Text style={styles.primaryButtonText}>Login/Signup</Text>
                                </TouchableOpacity>
                            </View>
                        )}
                        {session?.user && (
                            <View style={{ gap: 12 }}>
                                {loading && <Text style={{ color: '#334155' }}>Loading invoices…</Text>}
                                {error && <Text style={{ color: 'crimson' }}>{error}</Text>}
                                {info && !error && <Text style={{ color: '#0f766e' }}>{info}</Text>}
                                {invoices.length === 0 && !loading && (
                                    <Text style={{ color: '#334155' }}>No invoices found for the selected month.</Text>
                                )}

                                {/* Stats */}
                                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                                    <View style={styles.statCard}><Text style={styles.statTitle}>Invoices</Text><Text style={styles.statValue}>{monthStats.totalCount}</Text></View>
                                    <View style={styles.statCard}><Text style={styles.statTitle}>Paid</Text><Text style={styles.statValue}>{monthStats.paidCount}</Text></View>
                                    <View style={styles.statCard}><Text style={styles.statTitle}>Due</Text><Text style={styles.statValue}>{monthStats.dueCount}</Text></View>
                                    <View style={styles.statCard}><Text style={styles.statTitle}>Total</Text><Text style={styles.statValue}>{formatCurrency(monthStats.totalAmount, currencySymbol)}</Text></View>
                                </View>

                                {/* Desktop Table / Mobile Cards */}
                                {isMobile ? (
                                    /* Mobile Card Layout */
                                    <View style={{ gap: 12 }}>
                                        {filterInvoicesByPeriod(invoices)
                                            .sort((a, b) => (b.invoice_date || b.created_at || '').localeCompare(a.invoice_date || a.created_at || ''))
                                            .map((row, idx) => renderMobileInvoiceCard(row, idx))}
                                    </View>
                                ) : (
                                    /* Desktop Table Layout */
                                    <>
                                        <View style={styles.tableHeaderRow}>
                                            <Text style={[styles.tableHeaderCell, { flex: 1 }]}>#</Text>
                                            <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Invoice No</Text>
                                            <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Date</Text>
                                            <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Client</Text>
                                            <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Total</Text>
                                            <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Status</Text>
                                            <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Month</Text>
                                            <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Actions</Text>
                                        </View>
                                        {filterInvoicesByPeriod(invoices)
                                            .sort((a, b) => (b.invoice_date || b.created_at || '').localeCompare(a.invoice_date || a.created_at || ''))
                                            .map((row, idx) => (
                                                <View key={row.id} style={styles.tableRow}>
                                                    <Text style={[styles.tableCell, { flex: 1 }]}>{idx + 1}</Text>
                                                    <Text style={[styles.tableCell, { flex: 2 }]}>{row.invoice_number || '—'}</Text>
                                                    <Text style={[styles.tableCell, { flex: 2 }]}>{row.invoice_date || '—'}</Text>
                                                    <Text style={[styles.tableCell, { flex: 3 }]} numberOfLines={1}>{row?.client?.businessName || '—'}</Text>
                                                    <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(parseFloat(row.total) || 0, currencySymbol)}</Text>
                                                    <View style={[styles.tableCell, { flex: 1.5 }]}>
                                                        <Text style={[styles.pill, { backgroundColor: row.paid ? '#DCFCE7' : '#FEE2E2', color: row.paid ? '#065f46' : '#7F1D1D' }]}>{row.paid ? 'Paid' : 'Due'}</Text>
                                                    </View>
                                                    <View style={[styles.tableCell, { flex: 2 }]}>
                                                        <Text style={{ fontSize: 11, color: '#0f172a' }}>
                                                            {row.billing_month || getMonthKey(row.invoice_date, row.created_at)}
                                                        </Text>
                                                    </View>
                                                    <View style={[styles.tableCell, { flex: 3, flexDirection: 'row', gap: 4, flexWrap: 'wrap' }]}>
                                                        <Tooltip content="View invoice">
                                                            <TouchableOpacity style={styles.iconButton} onPress={() => viewInvoiceRow(row)}>
                                                                <Ionicons name="eye-outline" size={14} color="#334155" />
                                                            </TouchableOpacity>
                                                        </Tooltip>
                                                        <Tooltip content="Duplicate invoice">
                                                            <TouchableOpacity style={styles.iconButton} onPress={() => duplicateInvoiceRow(row)}>
                                                                <Ionicons name="copy-outline" size={14} color="#334155" />
                                                            </TouchableOpacity>
                                                        </Tooltip>
                                                        <Tooltip content="Download PDF">
                                                            <TouchableOpacity style={styles.iconButton} onPress={() => downloadInvoiceRow(row)}>
                                                                <Ionicons name="download-outline" size={14} color="#334155" />
                                                            </TouchableOpacity>
                                                        </Tooltip>
                                                        <Tooltip content={row.paid ? 'Mark as Unpaid' : 'Mark as Paid'}>
                                                            <TouchableOpacity style={styles.iconButton} onPress={() => togglePaidRow(row)}>
                                                                <Ionicons name={row.paid ? 'checkmark-done-outline' : 'alert-circle-outline'} size={14} color={row.paid ? '#065f46' : '#7F1D1D'} />
                                                            </TouchableOpacity>
                                                        </Tooltip>
                                                        <Tooltip content="Delete invoice">
                                                            <TouchableOpacity style={styles.iconButton} onPress={() => deleteInvoiceRow(row)}>
                                                                <Ionicons name="trash-outline" size={14} color="#7F1D1D" />
                                                            </TouchableOpacity>
                                                        </Tooltip>
                                                    </View>
                                                </View>
                                            ))}
                                    </>
                                )}
                            </View>
                        )}

                    </>
                )}

                {showQuotations && (
                    <>
                        {showInvoices && <View style={{ height: 1, backgroundColor: '#e5e7eb', marginVertical: 12 }} />}
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                            {/* Quotations Section header anchor */}
                            {React.createElement('div', { id: 'quotation-section', style: { position: 'absolute', top: -20 } })}
                            <Text style={{ fontSize: 20, fontWeight: '700', color: '#0f172a' }}>Quotation Maker</Text>
                            {!!session?.user && (
                                <View style={{ flexDirection: 'row', gap: 8 }}>
                                    <TouchableOpacity style={styles.outlineButton} onPress={refreshQuotations}>
                                        <Ionicons name="refresh-outline" size={16} color="#4F46E5" />
                                        <Text style={styles.outlineButtonText}>Refresh</Text>
                                    </TouchableOpacity>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <Text style={styles.helperText}>Month</Text>
                                        {React.createElement('select', {
                                            value: selectedQuoteMonth,
                                            onChange: (e) => setSelectedQuoteMonth(e.target.value),
                                            style: { padding: 6, borderRadius: 6, border: '1px solid #e5e7eb', backgroundColor: '#f8fafc' },
                                        }, [
                                            React.createElement('option', { key: 'all', value: 'all' }, 'All'),
                                            ...quoteMonthKeys.map((m) => React.createElement('option', { key: m, value: m }, m)),
                                        ])}
                                    </View>
                                </View>
                            )}
                        </View>
                        {!session?.user && (
                            <View style={styles.card}>
                                <Text style={{ fontSize: 14, color: '#334155' }}>Please login to view saved quotations.</Text>
                                <TouchableOpacity style={styles.primaryButton} onPress={openAuthModal}>
                                    <Text style={styles.primaryButtonText}>Login/Signup</Text>
                                </TouchableOpacity>
                            </View>
                        )}
                        {session?.user && (
                            <View style={{ gap: 12 }}>
                                {loadingQuotes && <Text style={{ color: '#334155' }}>Loading quotations…</Text>}
                                {errorQuotes && <Text style={{ color: 'crimson' }}>{errorQuotes}</Text>}
                                {infoQuotes && !errorQuotes && <Text style={{ color: '#0f766e' }}>{infoQuotes}</Text>}
                                {quotes.length === 0 && !loadingQuotes && (
                                    <Text style={{ color: '#334155' }}>No quotations found for the selected month.</Text>
                                )}

                                {/* Stats */}
                                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                                    <View style={styles.statCard}><Text style={styles.statTitle}>Quotations</Text><Text style={styles.statValue}>{quoteMonthStats.totalCount}</Text></View>
                                    <View style={styles.statCard}><Text style={styles.statTitle}>Total</Text><Text style={styles.statValue}>{formatCurrency(quoteMonthStats.totalAmount, currencySymbol)}</Text></View>
                                </View>

                                {/* Table header */}
                                <View style={styles.tableHeaderRow}>
                                    <Text style={[styles.tableHeaderCell, { flex: 1 }]}>#</Text>
                                    <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Quote No</Text>
                                    <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Date</Text>
                                    <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Client</Text>
                                    <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Total</Text>
                                    <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Status</Text>
                                    <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Month</Text>
                                    <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Actions</Text>
                                </View>

                                {/* Table rows */}
                                {quotes
                                    .filter((r) => selectedQuoteMonth === 'all' || (r.billing_month || getMonthKey(r.quote_date, r.created_at)) === selectedQuoteMonth)
                                    .map((row, idx) => (
                                        <View key={row.id} style={styles.tableRow}>
                                            <Text style={[styles.tableCell, { flex: 1 }]}>{idx + 1}</Text>
                                            <Text style={[styles.tableCell, { flex: 2 }]}>{row.quote_number || '—'}</Text>
                                            <Text style={[styles.tableCell, { flex: 2 }]}>{row.quote_date || '—'}</Text>
                                            <Text style={[styles.tableCell, { flex: 3 }]}>{row?.client?.businessName || '—'}</Text>
                                            <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(parseFloat(row.total) || 0, currencySymbol)} {row.currency || ''}</Text>
                                            <View style={[styles.tableCell, { flex: 2, position: 'relative' }]}>
                                                {React.createElement('select', {
                                                    value: (row.status || 'draft'),
                                                    onChange: (e) => { setStatusFlashId(row.id); setTimeout(() => setStatusFlashId(null), 1200); changeQuoteRowStatus(row, e.target.value); },
                                                    style: { padding: 6, borderRadius: 6, border: '1px solid #e5e7eb', backgroundColor: '#f8fafc' },
                                                }, STATUS_OPTIONS.map((s) => React.createElement('option', { key: s, value: s }, s[0].toUpperCase() + s.slice(1))))}
                                                {isWeb && statusFlashId === row.id && React.createElement('div', {
                                                    style: {
                                                        position: 'absolute',
                                                        left: -4,
                                                        right: -4,
                                                        top: -4,
                                                        bottom: -4,
                                                        backgroundColor: 'rgba(16,185,129,0.12)',
                                                        borderRadius: 8,
                                                        pointerEvents: 'none',
                                                    }
                                                })}
                                                {isWeb && statusFlashId === row.id && React.createElement('div', {
                                                    style: {
                                                        position: 'absolute',
                                                        right: -8,
                                                        top: -8,
                                                        backgroundColor: '#10b981',
                                                        color: '#ffffff',
                                                        borderRadius: 999,
                                                        padding: 4,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                                        opacity: 1,
                                                        transition: 'opacity 600ms ease',
                                                    }
                                                }, React.createElement('span', { style: { fontSize: 10, fontWeight: 700 } }, '✓'))}
                                            </View>
                                            <View style={[styles.tableCell, { flex: 2 }]}>
                                                {React.createElement('select', {
                                                    value: row.billing_month || getMonthKey(row.quote_date, row.created_at),
                                                    onChange: (e) => changeQuoteRowMonth(row, e.target.value),
                                                    style: { padding: 6, borderRadius: 6, border: '1px solid #e5e7eb', backgroundColor: '#f8fafc' },
                                                }, [
                                                    ...quoteMonthKeys.map((m) => React.createElement('option', { key: m, value: m }, m)),
                                                ])}
                                            </View>
                                            <View style={[styles.tableCell, { flex: 3, flexDirection: 'row', gap: 8, flexWrap: 'wrap' }]}>
                                                <Tooltip content="View quotation">
                                                    <TouchableOpacity style={styles.iconButton} onPress={() => viewQuoteRow(row)}>
                                                        <Ionicons name="eye-outline" size={16} color="#334155" />
                                                    </TouchableOpacity>
                                                </Tooltip>
                                                <Tooltip content="Download PDF">
                                                    <TouchableOpacity style={styles.iconButton} onPress={() => downloadQuoteRow(row)}>
                                                        <Ionicons name="download-outline" size={16} color="#334155" />
                                                    </TouchableOpacity>
                                                </Tooltip>
                                                <Tooltip content="Duplicate quotation">
                                                    <TouchableOpacity style={styles.iconButton} onPress={() => duplicateQuoteRow(row)}>
                                                        <Ionicons name="copy-outline" size={16} color="#334155" />
                                                    </TouchableOpacity>
                                                </Tooltip>
                                                {/* Email quotation icon removed */}
                                                <Tooltip content="Delete quotation">
                                                    <TouchableOpacity style={styles.iconButton} onPress={() => deleteQuoteRow(row)}>
                                                        <Ionicons name="trash-outline" size={16} color="#7F1D1D" />
                                                    </TouchableOpacity>
                                                </Tooltip>
                                            </View>
                                        </View>
                                    ))}
                            </View>
                        )}
                    </>
                )}
            </ScrollView>
            <Toast visible={toastVisible} message={toastMessage} type={toastType} onHide={() => setToastVisible(false)} />

            {/* Export Modal */}
            {showExportModal && (
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>Export Invoices</Text>
                            <TouchableOpacity onPress={() => setShowExportModal(false)}>
                                <Ionicons name="close" size={24} color="#64748b" />
                            </TouchableOpacity>
                        </View>
                        <View style={{ gap: 16 }}>
                            <View>
                                <Text style={styles.inputLabel}>Start Date (optional)</Text>
                                {isWeb ? React.createElement('input', {
                                    type: 'date',
                                    value: exportStartDate,
                                    onChange: (e) => setExportStartDate(e.target.value),
                                    style: { padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', width: '100%', fontSize: 14 },
                                }) : (
                                    <TextInput style={styles.input} value={exportStartDate} onChangeText={setExportStartDate} placeholder="YYYY-MM-DD" />
                                )}
                            </View>
                            <View>
                                <Text style={styles.inputLabel}>End Date (optional)</Text>
                                {isWeb ? React.createElement('input', {
                                    type: 'date',
                                    value: exportEndDate,
                                    onChange: (e) => setExportEndDate(e.target.value),
                                    style: { padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', width: '100%', fontSize: 14 },
                                }) : (
                                    <TextInput style={styles.input} value={exportEndDate} onChangeText={setExportEndDate} placeholder="YYYY-MM-DD" />
                                )}
                            </View>
                            <View>
                                <Text style={styles.inputLabel}>Format</Text>
                                <View style={{ flexDirection: 'row', gap: 12 }}>
                                    <TouchableOpacity
                                        style={[styles.formatButton, exportFormat === 'csv' && styles.formatButtonActive]}
                                        onPress={() => setExportFormat('csv')}
                                    >
                                        <Text style={[styles.formatButtonText, exportFormat === 'csv' && styles.formatButtonTextActive]}>CSV</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[styles.formatButton, exportFormat === 'xlsx' && styles.formatButtonActive]}
                                        onPress={() => setExportFormat('xlsx')}
                                    >
                                        <Text style={[styles.formatButtonText, exportFormat === 'xlsx' && styles.formatButtonTextActive]}>Excel</Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                            <Text style={styles.helperText}>Leave dates empty to export all invoices. Total: {invoices.length} invoices available.</Text>
                            <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'flex-end' }}>
                                <TouchableOpacity style={styles.outlineButton} onPress={() => setShowExportModal(false)}>
                                    <Text style={styles.outlineButtonText}>Cancel</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={styles.primaryButton} onPress={exportInvoices}>
                                    <Ionicons name="download-outline" size={16} color="#fff" />
                                    <Text style={styles.primaryButtonText}>Export</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </View>
            )}

            {/* Custom Date Range Modal */}
            {showCustomDateModal && (
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>Custom Date Range</Text>
                            <TouchableOpacity onPress={() => setShowCustomDateModal(false)}>
                                <Ionicons name="close" size={24} color="#64748b" />
                            </TouchableOpacity>
                        </View>
                        <View style={{ gap: 16 }}>
                            <View>
                                <Text style={styles.inputLabel}>Start Date</Text>
                                {isWeb ? React.createElement('input', {
                                    type: 'date',
                                    value: customStartDate,
                                    onChange: (e) => setCustomStartDate(e.target.value),
                                    style: { padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', width: '100%', fontSize: 14 },
                                }) : (
                                    <TextInput style={styles.input} value={customStartDate} onChangeText={setCustomStartDate} placeholder="YYYY-MM-DD" />
                                )}
                            </View>
                            <View>
                                <Text style={styles.inputLabel}>End Date</Text>
                                {isWeb ? React.createElement('input', {
                                    type: 'date',
                                    value: customEndDate,
                                    onChange: (e) => setCustomEndDate(e.target.value),
                                    style: { padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', width: '100%', fontSize: 14 },
                                }) : (
                                    <TextInput style={styles.input} value={customEndDate} onChangeText={setCustomEndDate} placeholder="YYYY-MM-DD" />
                                )}
                            </View>
                            <Text style={styles.helperText}>
                                Filter invoices between selected dates. Leave empty to not apply that boundary.
                            </Text>
                            <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'flex-end' }}>
                                <TouchableOpacity style={styles.outlineButton} onPress={() => {
                                    setCustomStartDate('');
                                    setCustomEndDate('');
                                    setFilterPeriod('all');
                                    setShowCustomDateModal(false);
                                }}>
                                    <Text style={styles.outlineButtonText}>Clear</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={styles.primaryButton} onPress={() => setShowCustomDateModal(false)}>
                                    <Ionicons name="checkmark-outline" size={16} color="#fff" />
                                    <Text style={styles.primaryButtonText}>Apply</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </View>
            )}

            <Footer />
            {/* Off-screen print container for Dashboard PDF export */}
            {isWeb && pdfProps && (
                <View nativeID="dashboard-quotation-print" style={{ position: 'absolute', left: -10000, top: 0 }} pointerEvents="none" aria-hidden="true" tabIndex={-1} importantForAccessibility="no-hide-descendants">
                    <QuotationPrint {...pdfProps} />
                </View>
            )}
            {/* Email quotation modal removed from Dashboard */}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        gap: 10,
    },
    primaryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: '#5B63F6',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
    },
    primaryButtonText: { color: '#fff', fontWeight: '700' },
    outlineButton: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8, borderWidth: 1, borderColor: '#4F46E5' },
    outlineButtonText: { color: '#4F46E5', fontWeight: '600', fontSize: 12 },
    helperText: { fontSize: 12, color: '#64748b' },
    tableHeaderRow: { flexDirection: 'row', backgroundColor: '#f3f4f6', borderWidth: 1, borderColor: '#e5e7eb', borderTopLeftRadius: 8, borderTopRightRadius: 8, paddingVertical: 10, paddingHorizontal: 8 },
    tableHeaderCell: { color: '#0f172a', fontWeight: '700', fontSize: 11 },
    tableRow: { flexDirection: 'row', backgroundColor: '#ffffff', borderLeftWidth: 1, borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#e5e7eb', paddingVertical: 8, paddingHorizontal: 8, alignItems: 'center' },
    tableCell: { fontSize: 11, color: '#0f172a' },
    pill: { paddingVertical: 4, paddingHorizontal: 8, borderRadius: 999, fontSize: 11, fontWeight: '700', alignSelf: 'flex-start' },
    iconButton: { padding: 4, borderRadius: 6, borderWidth: 1, borderColor: '#e5e7eb' },
    statCard: { backgroundColor: '#ffffff', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12, minWidth: 80, alignItems: 'center' },
    statTitle: { fontSize: 11, color: '#334155' },
    statValue: { fontSize: 13, color: '#0f172a', fontWeight: '700' },
    // Mobile card styles
    mobileCard: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        gap: 8,
    },
    mobileCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
    },
    mobileCardTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: '#0f172a',
    },
    mobileCardRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    mobileCardLabel: {
        fontSize: 12,
        color: '#64748b',
    },
    mobileCardValue: {
        fontSize: 12,
        color: '#0f172a',
    },
    mobileCardActions: {
        flexDirection: 'row',
        gap: 8,
        marginTop: 8,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: '#e5e7eb',
        justifyContent: 'center',
    },
    // Export modal styles
    modalOverlay: {
        ...(Platform.OS === 'web' ? { position: 'fixed' } : { position: 'absolute' }),
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: 16,
        padding: 24,
        width: '90%',
        maxWidth: 400,
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#0f172a',
    },
    inputLabel: {
        fontSize: 13,
        fontWeight: '600',
        color: '#334155',
        marginBottom: 6,
    },
    input: {
        borderWidth: 1,
        borderColor: '#e5e7eb',
        borderRadius: 8,
        padding: 10,
        fontSize: 14,
    },
    formatButton: {
        flex: 1,
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        alignItems: 'center',
    },
    formatButtonActive: {
        backgroundColor: '#5B63F6',
        borderColor: '#5B63F6',
    },
    formatButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#64748b',
    },
    formatButtonTextActive: {
        color: '#fff',
    },
});